/**
 * EntityInsightTiles — three answer tiles under the entity name on every
 * entity sheet (doc 147 §5.3 world-class redesign).
 *
 * Each tile is a complete answer: a headline number, a small viz, and a
 * deep-link CTA. Clean / empty states are positive signals — "no downstream"
 * and "all green" — not missing badges. The component is purely a render of
 * `entityInsights(index, entityId)`; no fetching, no async.
 */
import { useEffect, useState, type JSX } from 'react';
import type { InsightIndex } from '../state/entityInsights.js';
import { entityInsights } from '../state/entityInsights.js';
import { BlastBars } from '../components/BlastBars.js';
import { routeToHash } from '../state/hashRouter.js';
import { Icon } from '../components/Icon.js';
import {
  shouldShowTip,
  recordTipShown,
  dismissTip,
} from '../state/coachTip.js';

export interface EntityInsightTilesProps {
  index: InsightIndex;
  entityId: string;
  onOpenActions?: () => void;
}

export function EntityInsightTiles({
  index,
  entityId,
  onOpenActions,
}: EntityInsightTilesProps): JSX.Element {
  const insights = entityInsights(index, entityId);

  return (
    <div>
      <div className="entity-tiles" aria-label="Entity insight tiles">
        <BlastTile insights={insights} />
        <PatternsTile insights={insights} />
        <QualityTile insights={insights} />
        <SandboxTile insights={insights} />
        <HistoryTile insights={insights} />
      </div>
      {onOpenActions && (
        <button
          type="button"
          className="entity-more"
          onClick={onOpenActions}
          aria-label={`More actions for ${entityId}`}
        >
          <Icon name="more_horiz" size="sm" />
          <span>More actions for {entityId}</span>
          <kbd aria-hidden="true">E</kbd>
        </button>
      )}
    </div>
  );
}

function BlastTile({
  insights,
}: {
  insights: ReturnType<typeof entityInsights>;
}): JSX.Element {
  const { blast, entityId } = insights;
  const empty = blast.entityCount === 0;
  return (
    <section
      className="tile tile-blast"
      aria-label="Blast radius — what depends on this entity"
    >
      <header className="tile-head">
        <span className="tile-title">Blast radius</span>
        <CoachTip
          featureId="blast"
          text="What other entities would have to change if you change this one."
        />
        <kbd className="tile-shortcut" aria-hidden="true">
          i
        </kbd>
      </header>
      {empty ? (
        <div className="tile-body tile-empty">
          <Icon name="check_circle" size="md" />
          <span>No downstream references</span>
        </div>
      ) : (
        <div className="tile-body">
          <div className="tile-stat-line">
            <strong className="tile-stat">{plural(blast.entityCount, 'entity', 'entities')}</strong>
            <span className="tile-sep">·</span>
            <strong className="tile-stat">{plural(blast.domainCount, 'domain', 'domains')}</strong>
          </div>
          {blast.crossDomainHopCount > 0 && (
            <div className="tile-warn">
              <strong>{blast.crossDomainHopCount}</strong>{' '}
              <span>cross-domain {blast.crossDomainHopCount === 1 ? 'hop' : 'hops'}</span>
            </div>
          )}
          <div className="tile-viz">
            <BlastBars tiers={blast.tiers} ariaLabel="Blast tiers" />
          </div>
        </div>
      )}
      {!empty && (
        <div className="tile-peek" aria-hidden="true">
          <span className="tile-peek-label">Top affected:</span>{' '}
          <span className="mono">{blast.tiers[0]?.count ?? 0} at depth 1</span>
          {blast.tiers[1] && (
            <>
              {' · '}
              <span className="mono">{blast.tiers[1].count} at depth 2</span>
            </>
          )}
        </div>
      )}
      <footer className="tile-foot">
        <a
          className="tile-cta"
          href={routeToHash({ kind: 'impact', id: entityId })}
        >
          <Icon name="change_history" size="sm" />
          Open Impact <span aria-hidden="true">↗</span>
        </a>
      </footer>
    </section>
  );
}

function PatternsTile({
  insights,
}: {
  insights: ReturnType<typeof entityInsights>;
}): JSX.Element {
  const { patterns } = insights;
  const empty = patterns.readerCount === 0 && patterns.writerCount === 0;
  return (
    <section
      className="tile tile-patterns"
      aria-label="R/W patterns — functions touching this entity"
    >
      <header className="tile-head">
        <span className="tile-title">R/W patterns</span>
        <CoachTip
          featureId="patterns"
          text="Which Cloud Functions read or write this entity. Useful when planning a deprecation."
        />
        <kbd className="tile-shortcut" aria-hidden="true">
          p
        </kbd>
      </header>
      {empty ? (
        <div className="tile-body tile-empty">
          <Icon name="check_circle" size="md" />
          <span>No functions touch this entity</span>
        </div>
      ) : (
        <div className="tile-body">
          <div className="tile-stat-line">
            <strong className="tile-stat">{plural(patterns.readerCount, 'read', 'read')}</strong>
            <span className="tile-sep">·</span>
            <strong className="tile-stat">{plural(patterns.writerCount, 'write', 'write')}</strong>
          </div>
          <ul className="tile-list mono">
            {patterns.readers.slice(0, 3).map((r) => (
              <li key={`r-${r.name}`}>
                <span className="tile-rw" style={{ color: 'var(--info)' }}>
                  R
                </span>{' '}
                {r.name}
              </li>
            ))}
            {patterns.writers.slice(0, 3).map((w) => (
              <li key={`w-${w.name}`}>
                <span className="tile-rw" style={{ color: 'var(--caution)' }}>
                  W
                </span>{' '}
                {w.name}
              </li>
            ))}
          </ul>
        </div>
      )}
      {!empty && (
        <div className="tile-peek" aria-hidden="true">
          <span className="tile-peek-label">More:</span>{' '}
          <span className="mono">
            {patterns.readerCount + patterns.writerCount} total touchpoints
          </span>
          {patterns.readerCount > 3 && (
            <>
              {' · '}
              <span className="mono">+{patterns.readerCount - 3} readers hidden</span>
            </>
          )}
        </div>
      )}
      <footer className="tile-foot">
        <a className="tile-cta" href={routeToHash({ kind: 'patterns' })}>
          <Icon name="grid_view" size="sm" />
          Open Patterns <span aria-hidden="true">↗</span>
        </a>
      </footer>
    </section>
  );
}

function QualityTile({
  insights,
}: {
  insights: ReturnType<typeof entityInsights>;
}): JSX.Element {
  const { quality, entityId } = insights;
  const hasPii = quality.piiTags.length > 0;
  const allGreen =
    quality.lintFindings === 0 && quality.refactorProposals === 0 && !hasPii;
  return (
    <section className="tile tile-quality" aria-label="Quality — lint, refactor, PII signals">
      <header className="tile-head">
        <span className="tile-title">Quality</span>
        <CoachTip
          featureId="quality"
          text="Architectural lint, refactor candidates, and PII signals. Green = no concerns."
        />
        <kbd className="tile-shortcut" aria-hidden="true">
          q
        </kbd>
      </header>
      {allGreen ? (
        <div className="tile-body tile-empty">
          <Icon name="check_circle" size="md" />
          <span>All green</span>
        </div>
      ) : (
        <div className="tile-body">
          <ul className="tile-flaglist">
            <li>
              {quality.lintFindings === 0 ? (
                <>
                  <Icon name="check_circle" size="sm" />
                  <span>No lint findings</span>
                </>
              ) : (
                <>
                  <Icon name="error" size="sm" />
                  <span>
                    <strong>{quality.lintFindings}</strong>{' '}
                    lint {quality.lintFindings === 1 ? 'finding' : 'findings'}
                  </span>
                </>
              )}
            </li>
            <li>
              {quality.refactorProposals === 0 ? (
                <>
                  <Icon name="check_circle" size="sm" />
                  <span>No refactor flags</span>
                </>
              ) : (
                <>
                  <Icon name="lightbulb" size="sm" />
                  <span>
                    <strong>{quality.refactorProposals}</strong> refactor{' '}
                    {quality.refactorProposals === 1 ? 'proposal' : 'proposals'}
                  </span>
                </>
              )}
            </li>
            {hasPii && (
              <li className="tile-pii">
                <Icon name="lock" size="sm" />
                <span>
                  <strong>PII</strong> · {quality.piiTags.join(', ')}
                  {quality.piiDownstream > 0 && (
                    <>
                      {' '}
                      → {quality.piiDownstream} downstream
                    </>
                  )}
                </span>
              </li>
            )}
          </ul>
        </div>
      )}
      <footer className="tile-foot">
        {hasPii ? (
          <a className="tile-cta" href={routeToHash({ kind: 'compliance' })}>
            <Icon name="visibility" size="sm" />
            Compliance cone <span aria-hidden="true">↗</span>
          </a>
        ) : quality.lintFindings > 0 ? (
          <a className="tile-cta" href={routeToHash({ kind: 'lint' })}>
            <Icon name="rule" size="sm" />
            Open lint <span aria-hidden="true">↗</span>
          </a>
        ) : quality.refactorProposals > 0 ? (
          <a className="tile-cta" href={routeToHash({ kind: 'refactor' })}>
            <Icon name="lightbulb" size="sm" />
            Open refactor <span aria-hidden="true">↗</span>
          </a>
        ) : (
          <a className="tile-cta" href={routeToHash({ kind: 'lint' })}>
            <Icon name="rule" size="sm" />
            Open lint <span aria-hidden="true">↗</span>
          </a>
        )}
      </footer>
      <span className="visually-hidden" data-entity-id={entityId} />
    </section>
  );
}

function plural(n: number, singular: string, pluralForm: string): string {
  return `${n} ${n === 1 ? singular : pluralForm}`;
}

/**
 * SandboxTile (PR P1) — "what if I changed this?" cluster. Shows the
 * count of cloud functions writing to this entity (those become natural
 * simulation entry points) and CTAs into the What-If Sandbox + Sim views.
 */
function SandboxTile({
  insights,
}: {
  insights: ReturnType<typeof entityInsights>;
}): JSX.Element {
  const { sandbox, entityId } = insights;
  const hasTriggers = sandbox.simulatableTriggers > 0;
  return (
    <section className="tile tile-sandbox" aria-label="Sandbox — experiment with changes">
      <header className="tile-head">
        <span className="tile-title">Sandbox</span>
        <CoachTip
          featureId="sandbox"
          text="Propose schema changes safely, simulate triggers, or generate test data — all without touching production."
        />
        <kbd className="tile-shortcut" aria-hidden="true">
          s
        </kbd>
      </header>
      {hasTriggers ? (
        <div className="tile-body">
          <div className="tile-stat-line">
            <strong className="tile-stat">
              {plural(sandbox.simulatableTriggers, 'simulatable trigger', 'simulatable triggers')}
            </strong>
          </div>
          <ul className="tile-flaglist">
            <li>
              <Icon name="science" size="sm" />
              <span>Sandbox a schema change</span>
            </li>
            <li>
              <Icon name="bolt" size="sm" />
              <span>Simulate trigger cascade</span>
            </li>
            <li>
              <Icon name="data_object" size="sm" />
              <span>Generate test data</span>
            </li>
          </ul>
        </div>
      ) : (
        <div className="tile-body tile-empty">
          <Icon name="science" size="md" />
          <span>No simulatable triggers — still sandboxable</span>
        </div>
      )}
      <footer className="tile-foot">
        <a
          className="tile-cta"
          href={
            sandbox.canSeed
              ? routeToHash({ kind: 'sandbox', seedEntity: entityId })
              : routeToHash({ kind: 'sandbox' })
          }
        >
          <Icon name="science" size="sm" />
          Open Sandbox <span aria-hidden="true">↗</span>
        </a>
      </footer>
    </section>
  );
}

/**
 * HistoryTile (PR P1) — "how has this changed?" cluster. Surfaces the
 * entity's doc-anchor (numeric prefix of `docRef`) as a proxy for vintage,
 * with a CTA into the Drift Timeline focused on this entity.
 */
function HistoryTile({
  insights,
}: {
  insights: ReturnType<typeof entityInsights>;
}): JSX.Element {
  const { history, entityId } = insights;
  const hasDoc = history.earliestDoc !== null;
  return (
    <section className="tile tile-history" aria-label="History — how this entity has evolved">
      <header className="tile-head">
        <span className="tile-title">History</span>
        <CoachTip
          featureId="history"
          text="When this entity was introduced and how it has evolved across the documentation timeline."
        />
        <kbd className="tile-shortcut" aria-hidden="true">
          h
        </kbd>
      </header>
      {hasDoc ? (
        <div className="tile-body">
          <div className="tile-stat-line">
            <strong className="tile-stat">
              Doc {history.earliestDoc}
              {history.docSpan > 0 ? `–${history.latestDoc}` : ''}
            </strong>
          </div>
          <div className="tile-peek">
            <span className="tile-peek-label">Anchor:</span>{' '}
            <span className="mono">first appeared in doc {history.earliestDoc}</span>
          </div>
        </div>
      ) : (
        <div className="tile-body tile-empty">
          <Icon name="history" size="md" />
          <span>No history yet</span>
        </div>
      )}
      <footer className="tile-foot">
        <a className="tile-cta" href={routeToHash({ kind: 'drift', focus: entityId })}>
          <Icon name="history" size="sm" />
          Open Drift Timeline <span aria-hidden="true">↗</span>
        </a>
      </footer>
    </section>
  );
}

/**
 * CoachTip — a small `?` button next to a tile title that opens a one-line
 * explanation on click. Auto-fades-out after the user has seen it three
 * times (see `state/coachTip.ts`). Skips render when the feature has been
 * dismissed or threshold-met.
 */
function CoachTip({ featureId, text }: { featureId: string; text: string }): JSX.Element | null {
  const [show, setShow] = useState(false);
  // Decide visibility once at mount so dismiss feedback is instantaneous and
  // re-renders don't oscillate.
  const [visible, setVisible] = useState<boolean>(() => shouldShowTip(featureId));
  useEffect(() => {
    if (visible) recordTipShown(featureId);
  }, [visible, featureId]);
  if (!visible) return null;
  return (
    <span className="coach-tip-wrap">
      <button
        type="button"
        className="coach-tip-button"
        onClick={() => setShow((v) => !v)}
        aria-expanded={show}
        aria-label={`What is ${featureId}?`}
      >
        ?
      </button>
      {show && (
        <span className="coach-tip-popover" role="status">
          {text}
          <button
            type="button"
            className="coach-tip-dismiss"
            onClick={() => {
              dismissTip(featureId);
              setVisible(false);
            }}
            aria-label="Got it"
          >
            ✓ got it
          </button>
        </span>
      )}
    </span>
  );
}
